import { FC, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Grid, Typography } from '@mui/material';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import ControlledTextField from '../../../../components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from '../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledDateField from '../../../../components/controlledComponents/ControlledDateField.component';
import {
  TAcademicMaterial,
  IBook,
  ILabEquipment,
  IDigitalResource,
  IStationery,
  MaterialCategory,
  MaterialStatus,
  MaterialCondition,
  LabEquipmentType,
  DigitalResourceType,
  StationeryType,
  AccessLevel,
  LicenseType,
} from '../../../../../apis/academics/academicsMaterials.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TKeyValue } from 'src/utils/commonInterfaces';
import { useAcademicsMaterialsState } from '../../contexts/academicsMaterials.context';
import { useAcademicsMaterialsActions } from '../../contexts/academicsMaterials.context';
import dayjs, { Dayjs } from 'dayjs';
import { useAcademicsService } from 'src/apis/academics/useAcademicsService';
import { useQueryClient } from '@tanstack/react-query';
import { getAcademicsMaterialsKey } from '../../hooks/useAcademicsMaterialsHook';
import {
  isPickerDateRangeOrdered,
  zNumberFromInput,
  zOptionalNullablePickerDate,
  zOptionalNumberFromInput,
  zOptionalYearFromInput,
  zRequiredPickerDate,
} from 'src/utils/zodFormHelpers';

type MaterialFormData = {
  // Base fields
  name: string;
  description: string;
  category: MaterialCategory;
  status: MaterialStatus;
  condition: MaterialCondition;
  purchaseDate: Dayjs | null;
  lastMaintenanceDate?: Dayjs | null;
  location: string;
  quantity: number;
  minimumQuantity: number;
  supplier?: string;
  cost: number;
  tags: string;
  // Book fields
  isbn?: string;
  author?: string;
  publisher?: string;
  edition?: string;
  publicationYear?: number;
  subject?: string;
  gradeLevel?: string;
  language?: string;
  isDigital?: boolean | string;
  digitalFormat?: 'pdf' | 'epub' | 'mobi';
  digitalUrl?: string;
  // Lab Equipment fields
  equipmentType?: LabEquipmentType;
  modelNumber?: string;
  manufacturer?: string;
  serialNumber?: string;
  calibrationDate?: Dayjs | null;
  nextCalibrationDate?: Dayjs | null;
  powerRequirements?: string;
  safetyInstructions?: string;
  maintenanceFrequency?: number;
  lastMaintenance?: Dayjs | null;
  nextMaintenance?: Dayjs | null;
  maintenanceType?: string;
  maintenanceNotes?: string;
  isPortable?: boolean | string;
  requiresTraining?: boolean | string;
  // Digital Resource fields
  resourceType?: DigitalResourceType;
  format?: string;
  fileSize?: number;
  url?: string;
  accessLevel?: AccessLevel;
  licenseType?: LicenseType;
  expiryDate?: Dayjs | null;
  compatibleDevices?: string;
  requiresInternet?: boolean | string;
  // Stationery fields
  stationeryType?: StationeryType;
  brand?: string;
  color?: string;
  size?: string;
  isConsumable?: boolean | string;
  stationeryExpiryDate?: Dayjs | null;
};

const getMaterialFormSchema = (translateText: (key: string) => string) =>
  z
    .object({
    name: z.string().min(1, translateText('common.fieldRequired')),
    description: z.string().min(1, translateText('common.fieldRequired')),
    category: z.nativeEnum(MaterialCategory, {
      required_error: translateText('common.fieldRequired'),
    }),
    status: z.nativeEnum(MaterialStatus, {
      required_error: translateText('common.fieldRequired'),
    }),
    condition: z.nativeEnum(MaterialCondition, {
      required_error: translateText('common.fieldRequired'),
    }),
    purchaseDate: zRequiredPickerDate({
      requiredMessage: translateText('common.fieldRequired'),
      invalidMessage: translateText('common.dateInvalid'),
    }),
    lastMaintenanceDate: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
    location: z.string().min(1, translateText('common.fieldRequired')),
    quantity: zNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    minimumQuantity: zNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    supplier: z.string().optional(),
    cost: zNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    tags: z.string().optional(),
    // Book fields
    isbn: z.string().optional(),
    author: z.string().optional(),
    publisher: z.string().optional(),
    edition: z.string().optional(),
    publicationYear: zOptionalYearFromInput({
      message: translateText('finance.form.academicYearInvalid') || undefined,
    }),
    subject: z.string().optional(),
    gradeLevel: z.string().optional(),
    language: z.string().optional(),
    isDigital: z.union([z.boolean(), z.string()]).optional(),
    digitalFormat: z.enum(['pdf', 'epub', 'mobi']).optional(),
    digitalUrl: z.string().optional(),
    // Lab Equipment fields
    equipmentType: z.nativeEnum(LabEquipmentType).optional(),
    modelNumber: z.string().optional(),
    manufacturer: z.string().optional(),
    serialNumber: z.string().optional(),
    calibrationDate: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
    nextCalibrationDate: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
    powerRequirements: z.string().optional(),
    safetyInstructions: z.string().optional(),
    maintenanceFrequency: zOptionalNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    lastMaintenance: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
    nextMaintenance: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
    maintenanceType: z.string().optional(),
    maintenanceNotes: z.string().optional(),
    isPortable: z.union([z.boolean(), z.string()]).optional(),
    requiresTraining: z.union([z.boolean(), z.string()]).optional(),
    // Digital Resource fields
    resourceType: z.nativeEnum(DigitalResourceType).optional(),
    format: z.string().optional(),
    fileSize: zOptionalNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    url: z.string().optional(),
    accessLevel: z.nativeEnum(AccessLevel).optional(),
    licenseType: z.nativeEnum(LicenseType).optional(),
    expiryDate: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
    compatibleDevices: z.string().optional(),
    requiresInternet: z.union([z.boolean(), z.string()]).optional(),
    // Stationery fields
    stationeryType: z.nativeEnum(StationeryType).optional(),
    brand: z.string().optional(),
    color: z.string().optional(),
    size: z.string().optional(),
    isConsumable: z.union([z.boolean(), z.string()]).optional(),
    stationeryExpiryDate: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
  })
    .refine(
      (data) =>
        !data.lastMaintenanceDate ||
        isPickerDateRangeOrdered(data.purchaseDate, data.lastMaintenanceDate),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['lastMaintenanceDate'],
      }
    )
    .refine(
      (data) =>
        !data.calibrationDate ||
        !data.nextCalibrationDate ||
        isPickerDateRangeOrdered(
          data.calibrationDate,
          data.nextCalibrationDate
        ),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['nextCalibrationDate'],
      }
    )
    .refine(
      (data) =>
        !data.lastMaintenance ||
        !data.nextMaintenance ||
        isPickerDateRangeOrdered(data.lastMaintenance, data.nextMaintenance),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['nextMaintenance'],
      }
    );

const getCategoryOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: MaterialCategory.BOOK,
      value: translateText('academics.materials.categoryOptions.book') || 'Book',
    },
    {
      key: MaterialCategory.LAB_EQUIPMENT,
      value:
        translateText('academics.materials.categoryOptions.labEquipment') ||
        'Lab Equipment',
    },
    {
      key: MaterialCategory.DIGITAL_RESOURCE,
      value:
        translateText('academics.materials.categoryOptions.digitalResource') ||
        'Digital Resource',
    },
    {
      key: MaterialCategory.STATIONERY,
      value:
        translateText('academics.materials.categoryOptions.stationery') ||
        'Stationery',
    },
  ];

const getStatusOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: MaterialStatus.AVAILABLE,
      value:
        translateText('academics.materials.statuses.available') || 'Available',
    },
    {
      key: MaterialStatus.IN_USE,
      value: translateText('academics.materials.statuses.inUse') || 'In Use',
    },
    {
      key: MaterialStatus.MAINTENANCE,
      value:
        translateText('academics.materials.statuses.maintenance') ||
        'Maintenance',
    },
    {
      key: MaterialStatus.RETIRED,
      value: translateText('academics.materials.statuses.retired') || 'Retired',
    },
    {
      key: MaterialStatus.LOST,
      value: translateText('academics.materials.statuses.lost') || 'Lost',
    },
    {
      key: MaterialStatus.DAMAGED,
      value: translateText('academics.materials.statuses.damaged') || 'Damaged',
    },
  ];

const getConditionOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: MaterialCondition.NEW,
      value: translateText('academics.materials.conditions.new') || 'New',
    },
    {
      key: MaterialCondition.LIKE_NEW,
      value:
        translateText('academics.materials.conditions.likeNew') || 'Like New',
    },
    {
      key: MaterialCondition.GOOD,
      value: translateText('academics.materials.conditions.good') || 'Good',
    },
    {
      key: MaterialCondition.FAIR,
      value: translateText('academics.materials.conditions.fair') || 'Fair',
    },
    {
      key: MaterialCondition.POOR,
      value: translateText('academics.materials.conditions.poor') || 'Poor',
    },
  ];

const AddEditEachmaterialView: FC = () => {
  const { translateText } = useTranslation();
  const { academicsMaterialsAppState } = useAcademicsMaterialsState();
  const {
    setIsManageEachMaterialOpen,
    setIsEachMaterialLoading,
    setEachMaterialErrorMessage,
  } = useAcademicsMaterialsActions();
  const { addAcademicsMaterial, patchAcademicsMaterial } =
    useAcademicsService();
  const queryClient = useQueryClient();

  const {
    isManageEachMaterialOpen,
    selectedEachMaterial,
    isCreateEachMaterialMode,
    isEachMaterialLoading,
    eachMaterialErrorMessage,
    selectedMaterial,
    selectedMaterialGroupId,
  } = academicsMaterialsAppState;

  const materialFormSchema = getMaterialFormSchema(translateText);

  const defaultValues: MaterialFormData = {
    name: '',
    description: '',
    category: MaterialCategory.BOOK,
    status: MaterialStatus.AVAILABLE,
    condition: MaterialCondition.NEW,
    purchaseDate: dayjs(),
    lastMaintenanceDate: null,
    location: '',
    quantity: 0,
    minimumQuantity: 0,
    supplier: '',
    cost: 0,
    tags: '',
  };

  const methods = useForm<MaterialFormData>({
    resolver: zodResolver(materialFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = methods;

  const category = watch('category');

  useEffect(() => {
    if (selectedEachMaterial && isManageEachMaterialOpen) {
      const material = selectedEachMaterial;
      const baseData: MaterialFormData = {
        name: material.name || '',
        description: material.description || '',
        category: material.category || MaterialCategory.BOOK,
        status: material.status || MaterialStatus.AVAILABLE,
        condition: material.condition || MaterialCondition.NEW,
        purchaseDate: material.purchaseDate
          ? dayjs(material.purchaseDate)
          : dayjs(),
        lastMaintenanceDate: material.lastMaintenanceDate
          ? dayjs(material.lastMaintenanceDate)
          : null,
        location: material.location || '',
        quantity: material.quantity || 0,
        minimumQuantity: material.minimumQuantity || 0,
        supplier: material.supplier || '',
        cost: material.cost || 0,
        tags: Array.isArray(material.tags)
          ? material.tags.join(', ')
          : material.tags || '',
      };

      if (material.type === 'book') {
        const book = material as IBook;
        reset({
          ...baseData,
          isbn: book.isbn || '',
          author: book.author || '',
          publisher: book.publisher || '',
          edition: book.edition || '',
          publicationYear: book.publicationYear || undefined,
          subject: book.subject || '',
          gradeLevel: book.gradeLevel || '',
          language: book.language || '',
          isDigital: book.isDigital || false,
          digitalFormat: book.digitalFormat,
          digitalUrl: book.digitalUrl || '',
        });
      } else if (material.type === 'lab_equipment') {
        const lab = material as ILabEquipment;
        reset({
          ...baseData,
          equipmentType: lab.equipmentType,
          modelNumber: lab.modelNumber || '',
          manufacturer: lab.manufacturer || '',
          serialNumber: lab.serialNumber || '',
          calibrationDate: lab.calibrationDate
            ? dayjs(lab.calibrationDate)
            : null,
          nextCalibrationDate: lab.nextCalibrationDate
            ? dayjs(lab.nextCalibrationDate)
            : null,
          powerRequirements: lab.powerRequirements || '',
          safetyInstructions: lab.safetyInstructions || '',
          maintenanceFrequency: lab.maintenanceSchedule?.frequency || undefined,
          lastMaintenance: lab.maintenanceSchedule?.lastMaintenance
            ? dayjs(lab.maintenanceSchedule.lastMaintenance)
            : null,
          nextMaintenance: lab.maintenanceSchedule?.nextMaintenance
            ? dayjs(lab.maintenanceSchedule.nextMaintenance)
            : null,
          maintenanceType: lab.maintenanceSchedule?.maintenanceType || '',
          maintenanceNotes: lab.maintenanceSchedule?.maintenanceNotes || '',
          isPortable: lab.isPortable || false,
          requiresTraining: lab.requiresTraining || false,
        });
      } else if (material.type === 'digital_resource') {
        const digital = material as IDigitalResource;
        reset({
          ...baseData,
          resourceType: digital.resourceType,
          format: digital.format || '',
          fileSize: digital.fileSize || undefined,
          url: digital.url || '',
          accessLevel: digital.accessLevel,
          licenseType: digital.licenseType,
          expiryDate: digital.expiryDate ? dayjs(digital.expiryDate) : null,
          compatibleDevices: Array.isArray(digital.compatibleDevices)
            ? digital.compatibleDevices.join(', ')
            : '',
          requiresInternet: digital.requiresInternet || false,
        });
      } else if (material.type === 'stationery') {
        const stationery = material as IStationery;
        reset({
          ...baseData,
          stationeryType: stationery.stationeryType,
          brand: stationery.brand || '',
          color: stationery.color || '',
          size: stationery.size || '',
          isConsumable: stationery.isConsumable || false,
          stationeryExpiryDate: stationery.expiryDate
            ? dayjs(stationery.expiryDate)
            : null,
        });
      } else {
        reset(baseData);
      }
    } else if (isManageEachMaterialOpen) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEachMaterial, isManageEachMaterialOpen, reset]);

  const handleClose = () => {
    setIsManageEachMaterialOpen(false);
    setEachMaterialErrorMessage('');
    reset(defaultValues);
  };

  const onSubmitForm = async (data: MaterialFormData) => {
    setIsEachMaterialLoading(true);
    setEachMaterialErrorMessage('');

    try {
      const tagsArray = data.tags
        ? data.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter((tag) => tag.length > 0)
        : [];

      let materialData: TAcademicMaterial;

      if (data.category === MaterialCategory.BOOK) {
        materialData = {
          id: selectedEachMaterial?.id || '',
          type: 'book',
          name: data.name,
          description: data.description,
          category: data.category,
          status: data.status,
          condition: data.condition,
          purchaseDate: data.purchaseDate
            ? new Date(data.purchaseDate.toISOString())
            : new Date(),
          lastMaintenanceDate: data.lastMaintenanceDate
            ? new Date(data.lastMaintenanceDate.toISOString())
            : undefined,
          location: data.location,
          quantity: data.quantity,
          minimumQuantity: data.minimumQuantity,
          supplier: data.supplier,
          cost: data.cost,
          tags: tagsArray,
          actions: selectedEachMaterial?.actions || {
            view: true,
            edit: true,
            delete: true,
          },
          isbn: data.isbn || '',
          author: data.author || '',
          publisher: data.publisher || '',
          edition: data.edition || '',
          publicationYear: data.publicationYear || new Date().getFullYear(),
          subject: data.subject || '',
          gradeLevel: data.gradeLevel || '',
          language: data.language || '',
          isDigital:
            data.isDigital === 'true' || data.isDigital === true || false,
          digitalFormat: data.digitalFormat,
          digitalUrl: data.digitalUrl,
        } as IBook;
      } else if (data.category === MaterialCategory.LAB_EQUIPMENT) {
        materialData = {
          id: selectedEachMaterial?.id || '',
          type: 'lab_equipment',
          name: data.name,
          description: data.description,
          category: data.category,
          status: data.status,
          condition: data.condition,
          purchaseDate: data.purchaseDate
            ? new Date(data.purchaseDate.toISOString())
            : new Date(),
          lastMaintenanceDate: data.lastMaintenanceDate
            ? new Date(data.lastMaintenanceDate.toISOString())
            : undefined,
          location: data.location,
          quantity: data.quantity,
          minimumQuantity: data.minimumQuantity,
          supplier: data.supplier,
          cost: data.cost,
          tags: tagsArray,
          actions: selectedEachMaterial?.actions || {
            view: true,
            edit: true,
            delete: true,
          },
          equipmentType: data.equipmentType || LabEquipmentType.OTHER,
          modelNumber: data.modelNumber || '',
          manufacturer: data.manufacturer || '',
          serialNumber: data.serialNumber || '',
          calibrationDate: data.calibrationDate
            ? new Date(data.calibrationDate.toISOString())
            : undefined,
          nextCalibrationDate: data.nextCalibrationDate
            ? new Date(data.nextCalibrationDate.toISOString())
            : undefined,
          powerRequirements: data.powerRequirements,
          safetyInstructions: data.safetyInstructions || '',
          maintenanceSchedule: {
            frequency: data.maintenanceFrequency || 30,
            lastMaintenance: data.lastMaintenance
              ? new Date(data.lastMaintenance.toISOString())
              : new Date(),
            nextMaintenance: data.nextMaintenance
              ? new Date(data.nextMaintenance.toISOString())
              : new Date(),
            maintenanceType: data.maintenanceType || '',
            maintenanceNotes: data.maintenanceNotes,
          },
          isPortable:
            data.isPortable === 'true' || data.isPortable === true || false,
          requiresTraining:
            data.requiresTraining === 'true' ||
            data.requiresTraining === true ||
            false,
        } as ILabEquipment;
      } else if (data.category === MaterialCategory.DIGITAL_RESOURCE) {
        materialData = {
          id: selectedEachMaterial?.id || '',
          type: 'digital_resource',
          name: data.name,
          description: data.description,
          category: data.category,
          status: data.status,
          condition: data.condition,
          purchaseDate: data.purchaseDate
            ? new Date(data.purchaseDate.toISOString())
            : new Date(),
          lastMaintenanceDate: data.lastMaintenanceDate
            ? new Date(data.lastMaintenanceDate.toISOString())
            : undefined,
          location: data.location,
          quantity: data.quantity,
          minimumQuantity: data.minimumQuantity,
          supplier: data.supplier,
          cost: data.cost,
          tags: tagsArray,
          actions: selectedEachMaterial?.actions || {
            view: true,
            edit: true,
            delete: true,
          },
          resourceType: data.resourceType || DigitalResourceType.OTHER,
          format: data.format || '',
          fileSize: data.fileSize || 0,
          url: data.url || '',
          accessLevel: data.accessLevel || AccessLevel.PUBLIC,
          licenseType: data.licenseType || LicenseType.FREE,
          expiryDate: data.expiryDate
            ? new Date(data.expiryDate.toISOString())
            : undefined,
          compatibleDevices: data.compatibleDevices
            ? data.compatibleDevices.split(',').map((d) => d.trim())
            : [],
          requiresInternet:
            data.requiresInternet === 'true' ||
            data.requiresInternet === true ||
            false,
        } as IDigitalResource;
      } else if (data.category === MaterialCategory.STATIONERY) {
        materialData = {
          id: selectedEachMaterial?.id || '',
          type: 'stationery',
          name: data.name,
          description: data.description,
          category: data.category,
          status: data.status,
          condition: data.condition,
          purchaseDate: data.purchaseDate
            ? new Date(data.purchaseDate.toISOString())
            : new Date(),
          lastMaintenanceDate: data.lastMaintenanceDate
            ? new Date(data.lastMaintenanceDate.toISOString())
            : undefined,
          location: data.location,
          quantity: data.quantity,
          minimumQuantity: data.minimumQuantity,
          supplier: data.supplier,
          cost: data.cost,
          tags: tagsArray,
          actions: selectedEachMaterial?.actions || {
            view: true,
            edit: true,
            delete: true,
          },
          stationeryType: data.stationeryType || StationeryType.OTHER,
          brand: data.brand || '',
          color: data.color,
          size: data.size,
          isConsumable:
            data.isConsumable === 'true' || data.isConsumable === true || false,
          expiryDate: data.stationeryExpiryDate
            ? new Date(data.stationeryExpiryDate.toISOString())
            : undefined,
        } as IStationery;
      } else {
        throw new Error('Invalid material category');
      }

      // Call API to create or update material item
      const materialGroupId = selectedMaterialGroupId || selectedMaterial?.id;
      if (!materialGroupId) {
        throw new Error('Material group ID is required');
      }

      if (isCreateEachMaterialMode) {
        await addAcademicsMaterial(materialGroupId, materialData);
      } else {
        await patchAcademicsMaterial(materialGroupId, selectedEachMaterial?.id || '', materialData);
      }

      // Invalidate and refetch materials list
      queryClient.invalidateQueries({ queryKey: [getAcademicsMaterialsKey] });

      setIsEachMaterialLoading(false);
      handleClose();
    } catch (error) {
      setIsEachMaterialLoading(false);
      setEachMaterialErrorMessage(
        error instanceof Error
          ? error.message
          : translateText('common.error') || 'An error occurred'
      );
    }
  };

  const renderTypeSpecificFields = () => {
    if (category === MaterialCategory.BOOK) {
      return (
        <>
          <Grid item xs={12} md={6} data-testid="book-isbn-field">
            <ControlledTextField
              control={control}
              name="isbn"
              id="book-isbn"
              label="ISBN"
              errorMessage={errors.isbn?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="book-author-field">
            <ControlledTextField
              control={control}
              name="author"
              id="book-author"
              label={translateText('academics.library.author') || 'Author'}
              errorMessage={errors.author?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="book-publisher-field">
            <ControlledTextField
              control={control}
              name="publisher"
              id="book-publisher"
              label="Publisher"
              errorMessage={errors.publisher?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="book-edition-field">
            <ControlledTextField
              control={control}
              name="edition"
              id="book-edition"
              label="Edition"
              errorMessage={errors.edition?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="book-publication-year-field">
            <ControlledTextField
              control={control}
              name="publicationYear"
              id="book-publication-year"
              label="Publication Year"
              errorMessage={errors.publicationYear?.message}
              isGridItem={false}
              type="number"
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="book-subject-field">
            <ControlledTextField
              control={control}
              name="subject"
              id="book-subject"
              label="Subject"
              errorMessage={errors.subject?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="book-grade-level-field">
            <ControlledTextField
              control={control}
              name="gradeLevel"
              id="book-grade-level"
              label="Grade Level"
              errorMessage={errors.gradeLevel?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="book-language-field">
            <ControlledTextField
              control={control}
              name="language"
              id="book-language"
              label="Language"
              errorMessage={errors.language?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="book-digital-url-field">
            <ControlledTextField
              control={control}
              name="digitalUrl"
              id="book-digital-url"
              label="Digital URL"
              errorMessage={errors.digitalUrl?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="book-is-digital-field">
            <ControlledSelectField
              control={control}
              name="isDigital"
              id="book-is-digital"
              label="Is Digital"
              itemList={[
                { key: 'true', value: translateText('common.yes') || 'Yes' },
                { key: 'false', value: translateText('common.no') || 'No' },
              ]}
              errorMessage={errors.isDigital?.message}
              isGridItem={false}
            />
          </Grid>
        </>
      );
    }

    if (category === MaterialCategory.LAB_EQUIPMENT) {
      return (
        <>
          <Grid item xs={12} md={6} data-testid="lab-model-number-field">
            <ControlledTextField
              control={control}
              name="modelNumber"
              id="lab-model-number"
              label="Model Number"
              errorMessage={errors.modelNumber?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="lab-manufacturer-field">
            <ControlledTextField
              control={control}
              name="manufacturer"
              id="lab-manufacturer"
              label="Manufacturer"
              errorMessage={errors.manufacturer?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="lab-serial-number-field">
            <ControlledTextField
              control={control}
              name="serialNumber"
              id="lab-serial-number"
              label="Serial Number"
              errorMessage={errors.serialNumber?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="lab-calibration-date-field">
            <ControlledDateField
              control={control}
              name="calibrationDate"
              id="lab-calibration-date"
              label="Calibration Date"
              errorMessage={errors.calibrationDate?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            data-testid="lab-next-calibration-date-field"
          >
            <ControlledDateField
              control={control}
              name="nextCalibrationDate"
              id="lab-next-calibration-date"
              label="Next Calibration Date"
              errorMessage={errors.nextCalibrationDate?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="lab-power-requirements-field">
            <ControlledTextField
              control={control}
              name="powerRequirements"
              id="lab-power-requirements"
              label="Power Requirements"
              errorMessage={errors.powerRequirements?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} data-testid="lab-safety-instructions-field">
            <ControlledTextField
              control={control}
              name="safetyInstructions"
              id="lab-safety-instructions"
              label="Safety Instructions"
              errorMessage={errors.safetyInstructions?.message}
              isGridItem={false}
              multiline
              rows={3}
              fullWidth
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            data-testid="lab-maintenance-frequency-field"
          >
            <ControlledTextField
              control={control}
              name="maintenanceFrequency"
              id="lab-maintenance-frequency"
              label="Maintenance Frequency (days)"
              errorMessage={errors.maintenanceFrequency?.message}
              isGridItem={false}
              type="number"
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="lab-next-maintenance-field">
            <ControlledDateField
              control={control}
              name="nextMaintenance"
              id="lab-next-maintenance"
              label="Next Maintenance Date"
              errorMessage={errors.nextMaintenance?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} data-testid="lab-maintenance-notes-field">
            <ControlledTextField
              control={control}
              name="maintenanceNotes"
              id="lab-maintenance-notes"
              label="Maintenance Notes"
              errorMessage={errors.maintenanceNotes?.message}
              isGridItem={false}
              multiline
              rows={3}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="lab-is-portable-field">
            <ControlledSelectField
              control={control}
              name="isPortable"
              id="lab-is-portable"
              label="Is Portable"
              itemList={[
                { key: 'true', value: translateText('common.yes') || 'Yes' },
                { key: 'false', value: translateText('common.no') || 'No' },
              ]}
              errorMessage={errors.isPortable?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="lab-requires-training-field">
            <ControlledSelectField
              control={control}
              name="requiresTraining"
              id="lab-requires-training"
              label="Requires Training"
              itemList={[
                { key: 'true', value: translateText('common.yes') || 'Yes' },
                { key: 'false', value: translateText('common.no') || 'No' },
              ]}
              errorMessage={errors.requiresTraining?.message}
              isGridItem={false}
            />
          </Grid>
        </>
      );
    }

    if (category === MaterialCategory.DIGITAL_RESOURCE) {
      return (
        <>
          <Grid item xs={12} md={6} data-testid="digital-format-field">
            <ControlledTextField
              control={control}
              name="format"
              id="digital-format"
              label="Format"
              errorMessage={errors.format?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="digital-file-size-field">
            <ControlledTextField
              control={control}
              name="fileSize"
              id="digital-file-size"
              label="File Size (bytes)"
              errorMessage={errors.fileSize?.message}
              isGridItem={false}
              type="number"
            />
          </Grid>
          <Grid item xs={12} data-testid="digital-url-field">
            <ControlledTextField
              control={control}
              name="url"
              id="digital-url"
              label="URL"
              errorMessage={errors.url?.message}
              isGridItem={false}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="digital-expiry-date-field">
            <ControlledDateField
              control={control}
              name="expiryDate"
              id="digital-expiry-date"
              label="Expiry Date"
              errorMessage={errors.expiryDate?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            data-testid="digital-compatible-devices-field"
          >
            <ControlledTextField
              control={control}
              name="compatibleDevices"
              id="digital-compatible-devices"
              label="Compatible Devices (comma-separated)"
              errorMessage={errors.compatibleDevices?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            data-testid="digital-requires-internet-field"
          >
            <ControlledSelectField
              control={control}
              name="requiresInternet"
              id="digital-requires-internet"
              label="Requires Internet"
              itemList={[
                { key: 'true', value: translateText('common.yes') || 'Yes' },
                { key: 'false', value: translateText('common.no') || 'No' },
              ]}
              errorMessage={errors.requiresInternet?.message}
              isGridItem={false}
            />
          </Grid>
        </>
      );
    }

    if (category === MaterialCategory.STATIONERY) {
      return (
        <>
          <Grid item xs={12} md={6} data-testid="stationery-brand-field">
            <ControlledTextField
              control={control}
              name="brand"
              id="stationery-brand"
              label="Brand"
              errorMessage={errors.brand?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="stationery-color-field">
            <ControlledTextField
              control={control}
              name="color"
              id="stationery-color"
              label="Color"
              errorMessage={errors.color?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="stationery-size-field">
            <ControlledTextField
              control={control}
              name="size"
              id="stationery-size"
              label="Size"
              errorMessage={errors.size?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid item xs={12} md={6} data-testid="stationery-expiry-date-field">
            <ControlledDateField
              control={control}
              name="stationeryExpiryDate"
              id="stationery-expiry-date"
              label="Expiry Date"
              errorMessage={errors.stationeryExpiryDate?.message}
              isGridItem={false}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            data-testid="stationery-is-consumable-field"
          >
            <ControlledSelectField
              control={control}
              name="isConsumable"
              id="stationery-is-consumable"
              label="Is Consumable"
              itemList={[
                { key: 'true', value: translateText('common.yes') || 'Yes' },
                { key: 'false', value: translateText('common.no') || 'No' },
              ]}
              errorMessage={errors.isConsumable?.message}
              isGridItem={false}
            />
          </Grid>
        </>
      );
    }

    return null;
  };

  return (
    <div data-testid="add-edit-each-material-container">
      <StructuredModal
        isOpen={isManageEachMaterialOpen}
        onClose={handleClose}
        title={
          isCreateEachMaterialMode
            ? translateText('academics.materials.addMaterialDetail') ||
            'Add Material Detail'
            : translateText('academics.materials.editMaterial') ||
            'Edit Material'
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          isCreateEachMaterialMode
            ? translateText('common.save')
            : translateText('common.update')
        }
        isLoading={isEachMaterialLoading}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
        footerMessage={
          eachMaterialErrorMessage ? (
            <Typography
              color="error"
              variant="body2"
              data-testid="each-material-error-message"
            >
              {eachMaterialErrorMessage}
            </Typography>
          ) : null
        }
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            style={{ width: '100%' }}
            data-testid="each-material-form"
          >
            <Grid
              container
              spacing={{ xs: 2, sm: 3 }}
              padding={{ xs: 1, sm: 2 }}
              data-testid="each-material-form-container"
            >
              <Grid item xs={12} data-testid="each-material-details-section">
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  {translateText('academics.materials.materialDetails') ||
                    'Material Details'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="each-material-name-field"
              >
                <ControlledTextField
                  control={control}
                  name="name"
                  id="each-material-name"
                  label={translateText('academics.materials.name') || 'Name'}
                  errorMessage={errors.name?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="each-material-category-field"
              >
                <ControlledSelectField
                  control={control}
                  name="category"
                  id="each-material-category"
                  label={
                    translateText('academics.materials.categoryOptions.book') ||
                    'Category'
                  }
                  itemList={getCategoryOptions(translateText)}
                  errorMessage={errors.category?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="each-material-description-field">
                <ControlledTextField
                  control={control}
                  name="description"
                  id="each-material-description"
                  label={translateText('common.description') || 'Description'}
                  errorMessage={errors.description?.message}
                  required
                  isGridItem={false}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="each-material-status-field"
              >
                <ControlledSelectField
                  control={control}
                  name="status"
                  id="each-material-status"
                  label={
                    translateText('academics.materials.status') || 'Status'
                  }
                  itemList={getStatusOptions(translateText)}
                  errorMessage={errors.status?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="each-material-condition-field"
              >
                <ControlledSelectField
                  control={control}
                  name="condition"
                  id="each-material-condition"
                  label={
                    translateText('academics.materials.condition') ||
                    'Condition'
                  }
                  itemList={getConditionOptions(translateText)}
                  errorMessage={errors.condition?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="each-material-purchase-date-field"
              >
                <ControlledDateField
                  control={control}
                  name="purchaseDate"
                  id="each-material-purchase-date"
                  label={
                    translateText('academics.materials.purchaseDate') ||
                    'Purchase Date'
                  }
                  errorMessage={errors.purchaseDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="each-material-last-maintenance-date-field"
              >
                <ControlledDateField
                  control={control}
                  name="lastMaintenanceDate"
                  id="each-material-last-maintenance-date"
                  label={
                    translateText('academics.materials.lastMaintenanceDate') ||
                    'Last Maintenance Date'
                  }
                  errorMessage={errors.lastMaintenanceDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="each-material-location-field"
              >
                <ControlledTextField
                  control={control}
                  name="location"
                  id="each-material-location"
                  label={
                    translateText('academics.materials.location') || 'Location'
                  }
                  errorMessage={errors.location?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="each-material-quantity-field"
              >
                <ControlledTextField
                  control={control}
                  name="quantity"
                  id="each-material-quantity"
                  label={
                    translateText('academics.materials.totalCount') ||
                    'Quantity'
                  }
                  errorMessage={errors.quantity?.message}
                  required
                  isGridItem={false}
                  type="number"
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="each-material-minimum-quantity-field"
              >
                <ControlledTextField
                  control={control}
                  name="minimumQuantity"
                  id="each-material-minimum-quantity"
                  label={
                    translateText('academics.materials.minimumQuantity') ||
                    'Minimum Quantity'
                  }
                  errorMessage={errors.minimumQuantity?.message}
                  required
                  isGridItem={false}
                  type="number"
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="each-material-supplier-field"
              >
                <ControlledTextField
                  control={control}
                  name="supplier"
                  id="each-material-supplier"
                  label={
                    translateText('academics.materials.columns.supplier') ||
                    'Supplier'
                  }
                  errorMessage={errors.supplier?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="each-material-cost-field"
              >
                <ControlledTextField
                  control={control}
                  name="cost"
                  id="each-material-cost"
                  label={
                    translateText('academics.materials.columns.cost') || 'Cost'
                  }
                  errorMessage={errors.cost?.message}
                  required
                  isGridItem={false}
                  type="number"
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="each-material-tags-field"
              >
                <ControlledTextField
                  control={control}
                  name="tags"
                  id="each-material-tags"
                  label={
                    translateText('academics.materials.tags') ||
                    'Tags (comma-separated)'
                  }
                  errorMessage={errors.tags?.message}
                  isGridItem={false}
                  helperText={
                    translateText('academics.materials.tagsHelper') ||
                    'Separate multiple tags with commas'
                  }
                />
              </Grid>

              {category && (
                <>
                  <Grid item xs={12} data-testid="type-specific-fields-section">
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      {category === MaterialCategory.BOOK && 'Book Details'}
                      {category === MaterialCategory.LAB_EQUIPMENT &&
                        'Lab Equipment Details'}
                      {category === MaterialCategory.DIGITAL_RESOURCE &&
                        'Digital Resource Details'}
                      {category === MaterialCategory.STATIONERY &&
                        'Stationery Details'}
                    </Typography>
                  </Grid>
                  {renderTypeSpecificFields()}
                </>
              )}
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditEachmaterialView;
